import { RD_BASE } from './config';

// ── DeFiLlama 链 vs 协议路由 ─────────────────────────────
// 关键洞察（2026-06-18）：DefiLlama 把「Sui Foundation」当 placeholder 协议（slug=sui-foundation,
// tvl=null），真正的 Sui 数据在 chain endpoints 下。/summary/dexs/sui-foundation 会返 400。
//
// 走 chain endpoints 的判定：token symbol 在已知 L1 列表里 + 项目名贴近 chain 名
//   /v2/chains                          → 当前 TVL（snapshot）
//   /v2/historicalChainTvl/{chain}      → 历史 TVL（用于 week change）
//   /overview/dexs/{chain}              → DEX 24h/7d volume
//   /overview/fees/{chain}              → 链上 fees
//
// CoinGecko platforms key → DefiLlama chain slug（不一致的需要 mapping）
const CHAIN_SYMBOL_TO_LLAMA = {
  'SUI': 'sui',
  'APT': 'aptos',
  'SOL': 'solana',
  'ATOM': 'cosmos',
  'NEAR': 'near',
  'TRX': 'tron',
  'TON': 'ton',
  'ADA': 'cardano',
  'ALGO': 'algorand',
  'XLM': 'stellar',
  'FLOW': 'flow',
  'XTZ': 'tezos',
  'AVAX': 'avalanche',
  'ETH': 'ethereum',
  'BNB': 'bsc',
  'MATIC': 'polygon',
  'POL': 'polygon',
  'ARB': 'arbitrum',
  'OP': 'optimism',
  'SEI': 'sei',
  'INJ': 'injective',
  'DOT': 'polkadot',
  'KAS': 'kaspa',
  'XRP': 'xrp',
  'HBAR': 'hedera',
  'ICP': 'icp',
  'BCH': 'bitcoin-cash',
  'LTC': 'litecoin',
  'FTM': 'fantom',
  'S':   'sonic',
  'CELO': 'celo',
};

async function fetchDefiLlamaChain(chainSlug, projectName) {
  // 三个端点并行
  const [chainsList, dexData, feeData, tvlHist] = await Promise.all([
    fetch('https://api.llama.fi/v2/chains').then(r => r.json()).catch(() => []),
    fetch(`https://api.llama.fi/overview/dexs/${chainSlug}?excludeTotalDataChartBreakdown=true`).then(r => r.ok ? r.json() : null).catch(() => null),
    fetch(`https://api.llama.fi/overview/fees/${chainSlug}?excludeTotalDataChartBreakdown=true`).then(r => r.ok ? r.json() : null).catch(() => null),
    fetch(`https://api.llama.fi/v2/historicalChainTvl/${chainSlug}`).then(r => r.ok ? r.json() : null).catch(() => null),
  ]);
  const chainRow = (chainsList || []).find(c => (c.name || '').toLowerCase() === chainSlug.toLowerCase());
  if (!chainRow) return null;

  const results = { tvl: null, volume: null, fees: null, protocol: null };

  // TVL 当前 + 7 天变化（从 historical 算）
  let weekChange = null;
  if (Array.isArray(tvlHist) && tvlHist.length >= 8) {
    const cur = tvlHist[tvlHist.length - 1]?.tvl || 0;
    const old = tvlHist[tvlHist.length - 8]?.tvl || 0;
    if (old > 0) weekChange = ((cur - old) / old * 100).toFixed(1);
  }
  if (chainRow.tvl != null) {
    results.tvl = { current: chainRow.tvl, weekChange };
  }
  if (dexData?.total24h != null) {
    results.volume = { daily: dexData.total24h, weekly: dexData.total7d || null };
  }
  if (feeData?.total24h != null || feeData?.total30d != null) {
    results.fees = {
      daily:   feeData.total24h  || null,
      weekly:  feeData.total7d   || null,
      monthly: feeData.total30d  || null,
    };
  }
  results.protocol = {
    name: projectName || chainRow.name,
    slug: chainSlug,
    category: 'L1/Chain',
    chains: [chainRow.name],
    type: 'chain',
  };
  return results;
}

// ── DeFiLlama 链上数据 ───────────────────────────────
export async function fetchDefiLlama(tokenSymbol, projectName) {
  const results = { tvl: null, volume: null, fees: null, protocol: null };

  // ── 优先：L1 chain 项目走 chain endpoints ────────────────────────────
  // Sui / Aptos / Solana 这类项目在 /protocols 里只是 placeholder（tvl=null），
  // 真正数据在 /v2/chains + /overview/dexs/{chain}
  const symU = (tokenSymbol || '').toUpperCase().trim();
  const chainSlug = CHAIN_SYMBOL_TO_LLAMA[symU];
  if (chainSlug) {
    try {
      const chainRes = await fetchDefiLlamaChain(chainSlug, projectName);
      if (chainRes && (chainRes.tvl || chainRes.volume || chainRes.fees)) {
        return chainRes;
      }
    } catch { /* fall through to protocol matching */ }
  }

  try {
    // 优先走 proxy 缓存（1h TTL + Promise dedupe + SWR），直连 api.llama.fi 作 fallback
    const protocols = await fetch(`${RD_BASE}/api/llama-protocols`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(r.status)))
      .catch(async () => {
        try { return await fetch('https://api.llama.fi/protocols').then(r => r.json()); }
        catch { return []; }
      });

    // ── 品牌改名 + placeholder 反向映射 ────────────────────────────
    // 教训（2026-06-18）：第三方 API 的 slug 是他们运营手动维护的；
    // 既存在「项目改名 → 旧名映射到新 slug」也存在「placeholder slug 但没数据 → 映射到正确实体」。
    // 添加新条目前必须先 curl /protocols 验证 slug 真实存在。
    const ALIAS = {
      // 品牌改名
      "makerdao":      "sky-lending",
      "maker":         "sky-lending",
      "mkr":           "sky-lending",
      "compound":      "compound-v3",
      "compoundlabs":  "compound-v3",
      "sushi":         "sushiswap",
      "sushiswaplabs": "sushiswap",
      "curve":         "curve-dex",
      "curvefinance":  "curve-dex",
      "gmx":           "gmx-v2-perps",
      "pancakeswap":   "pancakeswap-amm",
      "pancakeswapdex":"pancakeswap-amm",
      "uniswap":       "uniswap-v3",
      "uniswaplabs":   "uniswap-v3",
      "aave":          "aave-v3",
      "aaveprotocol":  "aave-v3",
      "1inch":         "1inch",
      "lido":          "lido",
      "lidofinance":   "lido",
      "balancer":      "balancer-v2",
      "yearn":         "yearn-finance",
      "yearnfinance":  "yearn-finance",
      "velodrome":     "velodrome-v2",
      "aerodrome":     "aerodrome-slipstream",
      "stargate":      "stargate-v2",
      "synthetix":     "synthetix-v3",
      "morpho":        "morpho-blue",
      "morpholabs":    "morpho-blue",
      "jito":          "jito-liquid-staking",          // 多个 jito-* slug, liquid-staking 最大
      "jitofoundation":"jito-liquid-staking",
      "kamino":        "kamino-lend",
      "drift":         "drift-trade",
      "hyperliquid":   "hyperliquid-bridge",            // HYPE token 走 chain 路径; 这里兜底协议查询
      "ethena":        "ethena-usde",
      "ethenalabs":    "ethena-usde",
      "ondo":          "ondo-yield-assets",             // ondo-finance 不存在; yield-assets 最大
      "ondofinance":   "ondo-yield-assets",
      "raydium":       "raydium-amm",
      "orca":          "orca-dex",                      // orca-whirlpools 不存在; orca-dex 是真的
      "jupiter":       "jupiter-aggregator",
      // 已验证不在 /protocols: eigenlayer (改名为 restaking 类目, 但当前列表无该 slug, 留给 chain 检测如有 ETH symbol)
      // 已验证 Trader Joe 不在 /protocols (renamed to Joe / LFJ)
    };

    // ── 严格匹配逻辑（优先级从高到低）───────────────────────────────
    const name     = (projectName  || "").toLowerCase().trim();
    const sym      = (tokenSymbol  || "").toLowerCase().trim();
    const nameSlug = name.replace(/\s+/g, "-");
    const nameNoSp = name.replace(/\s+/g, "");

    let match = null;

    // 0. 品牌别名直接命中 slug
    const aliasSlug = ALIAS[nameNoSp] || ALIAS[sym];
    if (aliasSlug) match = protocols.find(p => p.slug?.toLowerCase() === aliasSlug);

    // 1. slug 完全等于项目名slug
    if (!match) match = protocols.find(p => p.slug?.toLowerCase() === nameSlug);
    // 2. slug 完全等于无空格名
    if (!match) match = protocols.find(p => p.slug?.toLowerCase() === nameNoSp);
    // 3. symbol 精确匹配
    if (!match && sym) match = protocols.find(p => (p.symbol || "").toLowerCase() === sym);
    // 4. name 精确匹配
    if (!match) match = protocols.find(p => (p.name || "").toLowerCase() === name);
    // 5. slug 以项目名开头，选 TVL 最大的版本（如 uniswap-v3）
    if (!match) {
      const cands = protocols.filter(p =>
        p.slug?.toLowerCase().startsWith(nameSlug + "-") ||
        p.slug?.toLowerCase().startsWith(nameNoSp + "-") ||
        p.slug?.toLowerCase() === nameSlug ||
        p.slug?.toLowerCase() === nameNoSp
      );
      if (cands.length > 0) match = cands.reduce((a, b) => ((b.tvl || 0) > (a.tvl || 0) ? b : a));
    }
    // 6. name 包含完整项目名词组（严格词边界）
    if (!match && name.length >= 4) {
      const cands = protocols.filter(p => {
        const pn = (p.name || "").toLowerCase();
        return pn === name ||
          pn.startsWith(name + " ") ||
          pn.startsWith(name + "-") ||
          pn.includes(" " + name + " ") ||
          pn.includes(" " + name);
      });
      if (cands.length > 0) match = cands.reduce((a, b) => ((b.tvl || 0) > (a.tvl || 0) ? b : a));
    }

    if (!match) return results;

    // ── 合理性校验：防止误匹配 ────────────────────────────────────────
    const mname = (match.name || "").toLowerCase().replace(/\s+/g, "");
    const hasOverlap =
      mname.includes(nameNoSp) ||
      nameNoSp.includes(mname.slice(0, Math.max(4, mname.length - 2))) ||
      (sym && (match.symbol || "").toLowerCase() === sym) ||
      !!aliasSlug;

    if (!hasOverlap) return results;

    results.protocol = {
      name: match.name,
      slug: match.slug,
      category: match.category,
      chains: (match.chains || []).slice(0, 3),
    };

    // TVL
    const tvlData = await fetch(`https://api.llama.fi/protocol/${match.slug}`)
      .then(r => r.json()).catch(() => null);
    if (tvlData) {
      const tvlArr = tvlData.tvl || [];
      const latest  = tvlArr[tvlArr.length - 1];
      const weekAgo = tvlArr[Math.max(0, tvlArr.length - 8)];
      const cur = latest?.totalLiquidityUSD || 0;
      const old = weekAgo?.totalLiquidityUSD || 0;
      results.tvl = {
        current: cur,
        weekChange: old > 0 ? ((cur - old) / old * 100).toFixed(1) : null,
      };
    }

    // DEX 成交量（可选）
    // NOTE: .ok check 必须有 — bridge/non-DEX slug 命中 /summary/dexs 会 400，
    // r.json() 会解 error body 后继续走（数据 undefined 是对的），但浏览器 console
    // 会留红色错误污染。这里 silent fallback 到 null（与 /summary/users 一致）。
    const volData = await fetch(
      `https://api.llama.fi/summary/dexs/${match.slug}?excludeTotalDataChartBreakdown=true`
    ).then(r => r.ok ? r.json() : null).catch(() => null);
    if (volData?.total24h) results.volume = { daily: volData.total24h, weekly: volData.total7d };

    // 协议手续费 + 收入（并行拉取，用于 P/S / P/Revenue 估值倍数计算）
    // fees   = 用户支付的全部手续费（protocol + LP/validators）
    // revenue= 协议自留部分（treasury / buyback / burn）
    // .ok check 同理 — bridge/Yield 类协议 /summary/fees 会 400
    const [feeData, revData] = await Promise.all([
      fetch(`https://api.llama.fi/summary/fees/${match.slug}?excludeTotalDataChartBreakdown=true`)
        .then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`https://api.llama.fi/summary/fees/${match.slug}?dataType=dailyRevenue&excludeTotalDataChartBreakdown=true`)
        .then(r => r.ok ? r.json() : null).catch(() => null),
    ]);
    if (feeData?.total24h || feeData?.total30d) results.fees = {
      daily:   feeData.total24h  || null,
      weekly:  feeData.total7d   || null,
      monthly: feeData.total30d  || null,
    };
    // revenue 为 0 = 协议无协议分成（EigenLayer/Morpho 等）；有值 = 协议自留部分
    if (revData?.total30d != null || revData?.total24h != null) results.revenue = {
      daily:   revData.total24h  || null,
      weekly:  revData.total7d   || null,
      monthly: revData.total30d  || null,
    };

    // Active Users（部分协议支持）
    const userData = await fetch(
      `https://api.llama.fi/summary/users/${match.slug}?excludeTotalDataChartBreakdown=true`
    ).then(r => r.ok ? r.json() : null).catch(() => null);
    if (userData?.total24h || userData?.total7d) {
      results.users = { daily: userData.total24h || null, weekly: userData.total7d || null };
    }

    // ── Chain placeholder 升级（2026-06-22）─────────────────────────────
    // DeFiLlama 给某些 L1（Plasma、Sui、Aptos 等）注册的 protocol 是个壳：
    //   protocolType="chain"、tvl≈0、fees≈0 — 真数据在 /v2/chains + /overview/*
    // 用户搜 Plasma 命中壳 → 看到 $41 fees，但点链接到 /protocol/plasma 是空页。
    // 此时尝试 fetchDefiLlamaChain 走 chain endpoint 拿到聚合数据。
    const isChainPlaceholder = (match.category === 'Chain' || match.protocolType === 'chain');
    const hasNoMeaningfulData =
      (!results.tvl?.current || results.tvl.current < 1000) &&
      (!results.fees?.monthly || results.fees.monthly < 5000) &&
      (!results.volume?.daily || results.volume.daily < 1000);
    if (isChainPlaceholder && hasNoMeaningfulData) {
      try {
        // chain endpoint 用首字母大写的 chain 名（DeFiLlama 约定）
        const chainName = (match.name || projectName || '').trim();
        if (chainName) {
          const chainAggRes = await fetchDefiLlamaChain(chainName, projectName);
          if (chainAggRes && (chainAggRes.tvl?.current > 1000 || chainAggRes.fees?.monthly > 5000)) {
            console.log(`[defillama] chain placeholder upgrade: ${match.slug} → chain/${chainName} (fees月 ${chainAggRes.fees?.monthly}, TVL ${chainAggRes.tvl?.current})`);
            // 单独拉 revenue：chain endpoint 也支持 dataType=dailyRevenue
            const chainRevRes = await fetch(
              `https://api.llama.fi/overview/fees/${chainName}?dataType=dailyRevenue&excludeTotalDataChartBreakdown=true`
            ).then(r => r.ok ? r.json() : null).catch(() => null);
            if (chainRevRes?.total24h != null || chainRevRes?.total30d != null) {
              chainAggRes.revenue = {
                daily:   chainRevRes.total24h || null,
                weekly:  chainRevRes.total7d  || null,
                monthly: chainRevRes.total30d || null,
              };
            }
            // 标记升级源，便于 UI 显示「数据来自 chain 聚合，链接走 chain 页」
            chainAggRes.protocol._upgradedFrom = match.slug;
            return chainAggRes;
          }
        }
      } catch (e) {
        console.warn(`[defillama] chain upgrade 失败 ${match.slug}:`, e?.message);
      }
    }

  } catch { /* DeFiLlama 失败不影响主流程 */ }
  return results;
}
